import type { PublicOrgInfo } from 'mock/generated/types';

import { getApiEnvironment } from 'src/shared/utils/environment';

import type { PartnerMetadata } from '../constants/partners';
import {
  DESIGNER_PARTNER_MAP,
  DesignerPartner,
  HOMEOWNER_ORG_MAP,
  NO_PARTNER_METADATA,
  PARTNER_CONFIGURATION_MAP,
} from '../constants/partners';
import type { Project } from '../graphql/generated';

import { warn } from './logging';

/**
 * Returns the homeowner org id for the current environment
 */
export const getHomeownerOrgId = () => {
  const environment = getApiEnvironment();

  if (!environment) {
    warn(
      'getPartnerConfiguration: unable to determine API environment required to retrieve partner configuration',
    );
    return undefined;
  }

  return HOMEOWNER_ORG_MAP[environment];
};

const getPartnerConfiguration = (orgId: number | undefined) => {
  const environment = getApiEnvironment();

  if (!environment) {
    warn(
      'getPartnerConfiguration: unable to determine API environment required to retrieve partner configuration',
    );
    return null;
  }

  const map = DESIGNER_PARTNER_MAP[environment];

  if (!map) {
    warn(
      `getPartnerConfiguration: no partner configuration found for environment "${environment}"`,
    );
    return null;
  }

  if (!orgId) return null;

  return map[orgId.toString()];
};

export const publicOrgInfoToPartnerMetadata = (
  publicOrgInfo:
    | Pick<
        PublicOrgInfo,
        'id' | 'brandLogoUrl' | 'displayName' | 'brandLogoUrlInvert'
      >
    | undefined
    | null,
): PartnerMetadata => {
  if (!publicOrgInfo) return NO_PARTNER_METADATA;

  const partner =
    getPartnerConfiguration(publicOrgInfo?.id) || DesignerPartner.NONE;

  return {
    partner,
    orgId: publicOrgInfo.id,
    displayName: publicOrgInfo.displayName,
    logoUrl: publicOrgInfo.brandLogoUrl ?? undefined,
    logoUrlInvert: publicOrgInfo.brandLogoUrlInvert ?? undefined,
    configuration: PARTNER_CONFIGURATION_MAP[partner],
  };
};

export type ProjectWithPartnerInfo = Pick<Project, 'publicOrgInfo'>;

export interface AiOriginalImageWithPartnerInfo {
  associatedProject?: ProjectWithPartnerInfo | null | undefined;
}
